import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/PageHeader';
import { PackageCheck, Lock, ClipboardCheck } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz, formatTimeInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { LoadStatusBadge } from '../../components/nursery/despatch/LoadStatusBadge';
import { LOAD_TYPE_LABELS, type PickLoadsResponse } from '../../types/nursery';

/**
 * /nursery/picking — the loads I can pick. A picker-only seat sees the loads
 * despatched from their farms; with no farm, the page says who to ask rather
 * than showing an empty list that reads as "nothing to do".
 */

function PickingLoads() {
  const tz = useOrgTimezone();
  const navigate = useNavigate();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', ['picker', 'operator', 'manager', 'admin']);
  const [data, setData] = useState<PickLoadsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasAccess) return;
    nurseryService.getPickLoads()
      .then((res) => (res.success ? setData(res.data) : setError(errorMessage(res, 'Could not load your loads'))))
      .catch((e) => setError(errorMessage(e, 'Could not load your loads')));
  }, [hasAccess]);

  if (licenseLoading) {
    return <div className="max-w-2xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Order Picker seat required</h2>
          <p className="text-sm text-neutral-500">Ask a nursery manager to give you the free Order Picker seat.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Picking"
        subtitle="Loads released for picking"
        icon={<PackageCheck className="h-6 w-6 text-white" />}
        actionButtons={
          // An Order Picker lands here, and counting stock is theirs to do too.
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery/stocktake')}>
            <ClipboardCheck className="h-4 w-4" /> Stocktake
          </Button>
        }
      />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}
        {!data ? (
          !error && <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : data.farms_assigned === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
            You are not assigned to any farm yet, so there is nothing for you to pick. Ask a nursery manager to choose
            your farms on the Order Pickers page.
          </div>
        ) : data.loads.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center text-sm text-neutral-500">
            No loads are waiting to be picked. They appear here once a manager releases them.
          </div>
        ) : (
          <ul className="space-y-3">
            {data.loads.map((l) => {
              const pct = l.item_count ? Math.round((l.picked_count / l.item_count) * 100) : 0;
              return (
                <li key={l.id}>
                  <Link to={`/nursery/picking/${l.id}`} className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-semibold text-neutral-900">{LOAD_TYPE_LABELS[l.load_type]}</span>
                      <span className="text-sm text-neutral-500">{l.load_number}</span>
                      <LoadStatusBadge status={l.status} />
                    </div>
                    <p className="text-sm text-neutral-700 mt-1 break-words">
                      {formatLocalDateInTz(l.despatch_date, tz)} · {l.despatch_farm_name}
                    </p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {l.order_count} order{l.order_count === 1 ? '' : 's'} · {l.total_trees.toLocaleString()} trees
                      {l.load_type === 'collection' && l.first_collection_at ? ` · first collection ${formatTimeInTz(l.first_collection_at, tz)}` : ''}
                    </p>
                    <div className="mt-2 h-2 rounded-full bg-neutral-100 overflow-hidden" aria-label={`${l.picked_count} of ${l.item_count} picked`}>
                      <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">{l.picked_count} of {l.item_count} items done</p>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

export default PickingLoads;
